"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ArrowLeft, Loader2, ImagePlus, Save, Upload, Eye, X, FileText, Trash2 } from "lucide-react";
import SEOHead from "../SEOHead";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import api from "../../lib/api";
import { parseImportedArticle } from "../../utils/blogImport";
import { BLOG_PROSE_CLASS } from "../../utils/blogProse";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

// Image attributes we let the editor (and the published HTML) keep. Out of the
// box Quill drops alt/width/style on images, so resizing and alt text wouldn't
// survive. Registering this custom image format makes them persist in the saved
// `contentHtml`. An inline `style="width:.."` also overrides the prose width on
// the public page, so resizing is actually reflected once published.
const IMAGE_ATTRS = ["alt", "title", "width", "height", "style"];
const BaseImage = Quill.import("formats/image");
if (BaseImage && !BaseImage.__btAttrsPatched) {
  class StyledImage extends BaseImage {
    static formats(domNode) {
      return IMAGE_ATTRS.reduce((acc, attr) => {
        if (domNode.hasAttribute(attr)) acc[attr] = domNode.getAttribute(attr);
        return acc;
      }, {});
    }
    format(name, value) {
      if (IMAGE_ATTRS.includes(name)) {
        if (value) this.domNode.setAttribute(name, value);
        else this.domNode.removeAttribute(name);
      } else {
        super.format(name, value);
      }
    }
  }
  StyledImage.__btAttrsPatched = true;
  Quill.register(StyledImage, true);
}

// Width presets offered when an image in the content is selected.
const IMAGE_WIDTHS = [
  { label: "25%", value: "25%" },
  { label: "50%", value: "50%" },
  { label: "75%", value: "75%" },
  { label: "Full", value: "" },
];

export default function BlogEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [keywords, setKeywords] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [published, setPublished] = useState(true);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [error, setError] = useState("");
  const coverInputRef = useRef(null);

  // Import (Markdown / HTML from Marblism etc.) + preview-before-publish state
  const [importText, setImportText] = useState("");
  const [importNote, setImportNote] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  // In-content image editing (resize / alt text / delete)
  const quillRef = useRef(null);
  const selectedImgElRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null); // { index, alt, width }

  // Load the post when editing. Prefer state passed from the dashboard,
  // otherwise fetch the admin list and find it.
  useEffect(() => {
    if (!isEdit) return;
    const apply = (post) => {
      setTitle(post.title || "");
      setContentHtml(post.content_html || "");
      setExcerpt(post.excerpt || "");
      setKeywords((post.keywords || []).join(", "));
      setCoverImageUrl(post.cover_image_url || "");
      setPublished(post.published !== false);
      setLoading(false);
    };
    if (location.state?.post) {
      apply(location.state.post);
      return;
    }
    api.blogAdmin
      .listPosts()
      .then((posts) => {
        const found = (posts || []).find((p) => p._id === id || p.id === id);
        if (found) apply(found);
        else navigate("/blog-admin", { replace: true });
      })
      .catch(() => navigate("/blog-admin", { replace: true }));
  }, [isEdit, id, location.state, navigate]);

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    setError("");
    try {
      const res = await api.blogAdmin.uploadImage(file);
      setCoverImageUrl(res.url);
    } catch (err) {
      setError(err.message || "Cover upload failed.");
    } finally {
      setUploadingCover(false);
    }
  };

  // Turn pasted/dropped Markdown or HTML into the editor fields.
  const applyImport = (raw, fileName = "") => {
    setError("");
    const parsed = parseImportedArticle(raw, fileName);
    if (!parsed.contentHtml && !parsed.title) {
      setError("Couldn't read any content from that file. Paste Markdown or HTML and try again.");
      return;
    }
    if (parsed.title) setTitle(parsed.title);
    setContentHtml(parsed.contentHtml);
    if (parsed.coverImageUrl) setCoverImageUrl(parsed.coverImageUrl);
    if (parsed.excerpt) setExcerpt(parsed.excerpt);
    setImportText("");
    setImportNote(
      `Imported ${parsed.format === "html" ? "HTML" : "Markdown"}${
        fileName ? ` from “${fileName}”` : ""
      }. Review below, then preview and publish.`,
    );
    setShowPreview(true);
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    if (/\.pdf$/i.test(file.name)) {
      setError(
        "PDF isn't supported — export the article as Markdown (.md) or HTML from Marblism and drop that instead.",
      );
      return;
    }
    try {
      const text = await file.text();
      applyImport(text, file.name);
    } catch {
      setError("Couldn't read that file.");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImportFile(file);
  };

  // --- In-content image editing ---------------------------------------------
  // Read a width like "50%" out of an image's inline style.
  const widthFromImg = (img) => {
    const m = (img.getAttribute("style") || "").match(/width:\s*([^;]+)/i);
    return m ? m[1].trim() : "";
  };

  // Visually mark which image is being edited.
  const outlineImg = (el, on) => {
    if (!el) return;
    el.style.outline = on ? "3px solid #98C454" : "";
    el.style.outlineOffset = on ? "2px" : "";
  };

  const deselectImage = () => {
    outlineImg(selectedImgElRef.current, false);
    selectedImgElRef.current = null;
    setSelectedImage(null);
  };

  // Clicking an image in the editor opens its edit panel. Handled via a click on
  // the editor wrapper (not a listener bound at mount) so it works identically
  // on the New page and the Edit page, where the editor mounts only after the
  // existing post has loaded.
  const handleEditorClick = (e) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const target = e.target;
    if (target?.tagName === "IMG" && editor.root.contains(target)) {
      const blot = Quill.find(target);
      if (!blot) return;
      outlineImg(selectedImgElRef.current, false);
      selectedImgElRef.current = target;
      outlineImg(target, true);
      setSelectedImage({
        index: editor.getIndex(blot),
        alt: target.getAttribute("alt") || "",
        width: widthFromImg(target),
      });
    } else if (editor.root.contains(target)) {
      // Clicked elsewhere inside the text area — deselect.
      deselectImage();
    }
  };

  const updateSelectedImage = (changes) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor || !selectedImage) return;
    const { index } = selectedImage;
    const next = { ...selectedImage, ...changes };
    if ("alt" in changes) {
      editor.formatText(index, 1, "alt", changes.alt || "", "user");
    }
    if ("width" in changes) {
      // Inline style so it survives sanitisation and overrides the prose width.
      editor.formatText(index, 1, "style", changes.width ? `width: ${changes.width}` : "", "user");
    }
    setSelectedImage(next);
    setContentHtml(editor.root.innerHTML);
  };

  const deleteSelectedImage = () => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor || !selectedImage) return;
    editor.deleteText(selectedImage.index, 1, "user");
    setSelectedImage(null);
    setContentHtml(editor.root.innerHTML);
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim() || !contentHtml.trim() || contentHtml === "<p><br></p>") {
      setError("Title and content are required.");
      return;
    }
    setSaving(true);
    const payload = {
      title: title.trim(),
      contentHtml,
      excerpt: excerpt.trim(),
      metaDescription: excerpt.trim(),
      keywords,
      coverImageUrl,
      published,
    };
    try {
      if (isEdit) await api.blogAdmin.updatePost(id, payload);
      else await api.blogAdmin.createPost(payload);
      navigate("/blog-admin", { replace: true });
    } catch (err) {
      setError(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const editorKey = useMemo(() => (isEdit ? `edit-${id}` : "new"), [isEdit, id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#252F4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <SEOHead title={isEdit ? "Edit Post" : "New Post"} noindex nofollow disableCanonical />

      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("/blog-admin")}
          className="flex items-center gap-2 text-gray-500 hover:text-[#252F4F] mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to posts
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-[#252F4F] mb-6">
            {isEdit ? "Edit post" : "New post"}
          </h1>

          {error && (
            <div className="mb-5 rounded-lg bg-red-50 text-red-700 text-sm px-4 py-3">{error}</div>
          )}

          {/* Import from Marblism (Markdown / HTML) */}
          <div className="mb-6 rounded-xl border border-[#98C454]/40 bg-[#98C454]/5 p-5">
            <div className="flex items-center gap-2 mb-1">
              <Upload className="h-4 w-4 text-[#252F4F]" />
              <p className="font-semibold text-[#252F4F]">Import an article</p>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Drop or paste a <strong>Markdown (.md)</strong> or <strong>HTML</strong> file exported
              from Marblism. The title, cover image and content fill in automatically — then preview
              and publish. (Markdown is recommended; PDF isn&apos;t supported.)
            </p>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
                dragActive
                  ? "border-[#98C454] bg-[#98C454]/10"
                  : "border-gray-300 bg-white hover:border-[#98C454]"
              }`}
            >
              <FileText className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                Drag &amp; drop a .md or .html file, or{" "}
                <span className="text-[#252F4F] font-medium underline">browse</span>
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".md,.markdown,.html,.htm,text/markdown,text/html"
                onChange={(e) => {
                  handleImportFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
                className="hidden"
              />
            </div>

            <div className="mt-3">
              <Textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="…or paste Markdown / HTML here"
                rows={4}
                className="bg-white"
              />
              <div className="flex justify-end mt-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={!importText.trim()}
                  onClick={() => applyImport(importText)}
                >
                  Import pasted text
                </Button>
              </div>
            </div>

            {importNote && (
              <p className="mt-3 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
                {importNote}
              </p>
            )}
          </div>

          <form onSubmit={save} className="space-y-6">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="A great massage therapy guide"
                className="mt-1.5"
                required
              />
            </div>

            <div>
              <Label>Cover image</Label>
              <div className="mt-1.5 flex items-center gap-4">
                {coverImageUrl ? (
                  <img
                    src={coverImageUrl}
                    alt="cover"
                    className="h-20 w-32 object-cover rounded-lg border"
                  />
                ) : (
                  <div className="h-20 w-32 rounded-lg border border-dashed flex items-center justify-center text-gray-400">
                    <ImagePlus className="h-6 w-6" />
                  </div>
                )}
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCover}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingCover}
                >
                  {uploadingCover ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : coverImageUrl ? (
                    "Replace image"
                  ) : (
                    "Upload image"
                  )}
                </Button>
              </div>
            </div>

            <div>
              <Label>Content</Label>
              <p className="text-xs text-gray-400 mt-0.5 mb-1.5">
                Tip: click any image in the content to resize it, edit its alt text, or delete it.
              </p>
              <div className="mt-1.5 bg-white rounded-lg" onClick={handleEditorClick}>
                <ReactQuill
                  ref={quillRef}
                  key={editorKey}
                  theme="snow"
                  value={contentHtml}
                  onChange={setContentHtml}
                  modules={QUILL_MODULES}
                />
              </div>

              {/* Selected-image editor: resize / alt text / delete */}
              {selectedImage && (
                <div className="mt-3 rounded-xl border border-[#252F4F]/20 bg-[#252F4F]/[0.03] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-[#252F4F] text-sm">Selected image</p>
                    <button
                      type="button"
                      onClick={() => setSelectedImage(null)}
                      className="p-1 text-gray-400 hover:text-[#252F4F]"
                      aria-label="Deselect image"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mb-3">
                    <Label className="text-xs text-gray-500">Width</Label>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {IMAGE_WIDTHS.map((w) => {
                        const active = (selectedImage.width || "") === w.value;
                        return (
                          <Button
                            key={w.label}
                            type="button"
                            size="sm"
                            variant={active ? "default" : "outline"}
                            className={active ? "bg-[#252F4F] hover:bg-[#1e2640]" : ""}
                            onClick={() => updateSelectedImage({ width: w.value })}
                          >
                            {w.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="img-alt" className="text-xs text-gray-500">
                      Alt text (for SEO &amp; accessibility)
                    </Label>
                    <Input
                      id="img-alt"
                      value={selectedImage.alt}
                      onChange={(e) => updateSelectedImage({ alt: e.target.value })}
                      placeholder="Describe the image"
                      className="mt-1.5 bg-white"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={deleteSelectedImage}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Delete image
                  </Button>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="excerpt">Excerpt / meta description</Label>
              <Textarea
                id="excerpt"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Short summary shown on the blog list and in search results."
                className="mt-1.5"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="keywords">Keywords (comma separated)</Label>
              <Input
                id="keywords"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="massage, wellness, RMT"
                className="mt-1.5"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border px-4 py-3">
              <div>
                <p className="font-medium text-[#252F4F]">Published</p>
                <p className="text-sm text-gray-500">
                  When on, the post is visible (and indexed) on the public blog.
                </p>
              </div>
              <Switch checked={published} onCheckedChange={setPublished} />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate("/blog-admin")}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPreview(true)}
                disabled={!contentHtml.trim() || contentHtml === "<p><br></p>"}
              >
                <Eye className="h-4 w-4 mr-2" /> Preview
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#252F4F] hover:bg-[#1e2640]">
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" /> {isEdit ? "Save changes" : "Publish post"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Preview-before-publish modal — mirrors the public blog page */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-4xl w-full my-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-[#252F4F]" />
                <span className="font-semibold text-[#252F4F]">Preview</span>
                <span className="text-xs text-gray-400">This is how the published post will look.</span>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1.5 text-gray-400 hover:text-[#252F4F]"
                aria-label="Close preview"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 sm:px-10 py-8">
              <h1 className="text-4xl font-bold text-[#252F4F] mb-6">
                {title || "Untitled post"}
              </h1>
              {coverImageUrl && (
                <img
                  src={coverImageUrl}
                  alt={title}
                  className="w-full max-h-[460px] object-cover rounded-2xl shadow-md mb-10"
                />
              )}
              <div
                className={BLOG_PROSE_CLASS}
                dangerouslySetInnerHTML={{
                  __html: contentHtml.replace(/<h1[^>]*>.*?<\/h1>/i, ""),
                }}
              />
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4 sticky bottom-0 bg-white rounded-b-2xl">
              <Button type="button" variant="outline" onClick={() => setShowPreview(false)}>
                Keep editing
              </Button>
              <Button
                type="button"
                disabled={saving}
                onClick={save}
                className="bg-[#252F4F] hover:bg-[#1e2640]"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" /> {isEdit ? "Save changes" : "Publish post"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
